import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { portalSession, StoredPatientContext } from './portalSession';
import { requestSession } from './requestSession';

// Custom-auth client for the healthcom-schedule patient portal.
// Replaces the Amplify-based Cognito flow. All four endpoints are
// public (the wrapper accepts no scope); auth is done by exchanging
// short-lived OTP challenges for long-lived session tokens.

export type AuthMatch = {
  patientId: string;
  fname: string;
  lname: string;
  clinicId: number;
  clinicName: string;
};

const NOAUTH = 'api-noauth';

const startOtp = async (input: {
  contact: string;
  contactType: 'email' | 'phone';
}): Promise<{ challengeId: string; expiresAt: string; message: string }> => {
  return ChiroUpAPI.post(NOAUTH, '/auth/otp/start', { body: input });
};

const verifyOtp = async (input: {
  challengeId: string;
  code: string;
}): Promise<{ challengeId: string; matches: AuthMatch[] }> => {
  return ChiroUpAPI.post(NOAUTH, '/auth/otp/verify', { body: input });
};

const startSession = async (input: {
  challengeId: string;
  patientId: string;
  clinicId: number;
}): Promise<{
  token: string;
  expiresAt: string;
  patient: StoredPatientContext;
}> => {
  const res = await ChiroUpAPI.post(NOAUTH, '/auth/session/start', { body: input });

  if (res?.token) {
    portalSession.setToken(res.token, res.expiresAt);
  }

  if (res?.patient) {
    portalSession.setPatient(res.patient);
  }

  return res;
};

// Legacy username/password login. Authenticates against the original
// healthcom-app Cognito patient pool server-side. On the happy path
// returns a patient-scoped portal session bound to the row matched
// by the legacy username. When Cognito needs a follow-up
// (NEW_PASSWORD_REQUIRED — admin-created users that haven't set a
// password yet) the response carries the Cognito Session token; the
// caller passes it back to `legacyNewPassword` along with the
// patient's chosen new password to finish the flow.
type LegacyLoginSuccess = {
  token: string;
  expiresAt: string;
  patient: StoredPatientContext;
};

type LegacyLoginNewPasswordRequired = {
  challenge: 'NEW_PASSWORD_REQUIRED';
  session: string;
  username: string;
};

export type LegacyLoginResult = LegacyLoginSuccess | LegacyLoginNewPasswordRequired;

export const isNewPasswordRequired = (
  res: LegacyLoginResult
): res is LegacyLoginNewPasswordRequired =>
  (res as LegacyLoginNewPasswordRequired)?.challenge === 'NEW_PASSWORD_REQUIRED' &&
  typeof (res as LegacyLoginNewPasswordRequired)?.session === 'string';

const legacyLogin = async (input: {
  username: string;
  password: string;
}): Promise<LegacyLoginResult> => {
  const res = await ChiroUpAPI.post(NOAUTH, '/auth/legacy/login', { body: input });

  if (isNewPasswordRequired(res)) return res;

  if (res?.token) {
    portalSession.setToken(res.token, res.expiresAt);
  }

  if (res?.patient) {
    portalSession.setPatient(res.patient);
  }

  return res;
};

const legacyNewPassword = async (input: {
  username: string;
  newPassword: string;
  session: string;
}): Promise<LegacyLoginSuccess> => {
  const res = await ChiroUpAPI.post(NOAUTH, '/auth/legacy/login/new-password', {
    body: input,
  });

  if (res?.token) {
    portalSession.setToken(res.token, res.expiresAt);
  }

  if (res?.patient) {
    portalSession.setPatient(res.patient);
  }

  return res;
};

const logout = async (): Promise<void> => {
  // Capture the in-flight session before we touch anything — the
  // server-side revoke must use *this* token even if a request-flow
  // token is also stored. Clear local state up front so a network
  // failure on the revoke can't leave the client in a half-signed-in
  // state with a fresh request token bleeding through.
  const portalToken = portalSession.getToken();

  portalSession.clear();
  requestSession.clear();

  if (!portalToken) return;

  try {
    await fetch(`${import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string}/auth/session/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${portalToken}`,
      },
      body: '{}',
    });
  } catch {
    /* server-side revoke failure is fine — local state is already
       cleared so subsequent requests will look unauthenticated. */
  }
};

export const authService = {
  startOtp,
  verifyOtp,
  startSession,
  legacyLogin,
  legacyNewPassword,
  logout,
};
